/**
 * THE COSTUME TRACKER (Bobbie, 2026-11-03): "can the costume check list show
 * up on the dancers pages and the profile pages for each dancer as well? and
 * in the menu each partent teacher and manager diretion front desk - all
 * roles have a costume tracker for what they mange, front desk admin see
 * their kids first then an admin view option where they can mark off costume
 * pick ups", and "it should clearly show who picked up the costume who
 * checked it in and exactly when".
 *
 * ONE CHECKLIST PER DANCER, one row per costume she wears: Ordered, Arrived,
 * Ready for pickup (each with who marked it and when, from the class card's
 * trail in costumeStatusLog), Tried on and the family's pieces in hand (the
 * batch 2 marks), then Picked up with who and exactly when, in studio time.
 *
 * WHO SEES WHAT is decided on the server (netlify/functions/costumeTracker.mjs
 * and lib/costumeTrackerCore.mjs) from the caller's users doc, read fresh:
 *   mine     her own dancers (a grown-up), or her own costumes (a dancer, a
 *            Jr Staff on either sign-in)
 *   classes  the classes a teacher teaches or assists
 *   all      every dancer, for the Director, Managers and the front desk, who
 *            mark pickups from here
 * This file holds what both sides share: the per-dancer step answers (the
 * same reading Kim's Costume report gives), the views, the words, the wire
 * reader and the search.
 *
 * PURE. No React, no Firebase, and only .ts imports with their extension, so
 * Node runs it directly in tests and inside the Netlify Function.
 */
import {
  isCostumeTrackedForPickup,
  pickupStateFor,
  type CostumePickupCard,
} from './costumePickupState.ts';
import { inHandFrom, type CostumeInHand, type FamilyPiece } from './costumeInHand.ts';
import { studioStamp, type CostumeTrailStamp } from './costumeTrail.ts';
import { costumePickupStateText } from './costumePickupWording.ts';

export type TrackerView = 'mine' | 'classes' | 'all';
export const TRACKER_VIEWS: readonly TrackerView[] = ['mine', 'classes', 'all'];

/** The roles that see every dancer and mark pickups: the pickup desk. */
export const TRACKER_ALL_ROLES: readonly string[] = ['director', 'manager', 'foh'];
/** The roles whose own classes are a view of their own. */
export const TRACKER_CLASS_ROLES: readonly string[] = ['company'];

/**
 * Which views a person has, in the order the page shows them. Her own kids
 * come FIRST (Bobbie: "front desk admin see their kids first then an admin
 * view option"). A Jr Staff is a dancer here on either sign-in: her own
 * costumes, nothing else (DECISIONS 26 and 27).
 */
export function trackerViewsFor(input: { role: string | null | undefined; status: string | null | undefined; hasOwn: boolean }): TrackerView[] {
  if (input.status !== 'active') return input.hasOwn ? ['mine'] : [];
  const role = String(input.role ?? '');
  const views: TrackerView[] = [];
  if (input.hasOwn) views.push('mine');
  if (TRACKER_CLASS_ROLES.includes(role)) views.push('classes');
  if (TRACKER_ALL_ROLES.includes(role)) views.push('all');
  return views;
}

export function trackerViewLabel(view: TrackerView, input: { dancer?: boolean } = {}): string {
  if (view === 'mine') return input.dancer ? 'My costumes' : 'My dancers';
  if (view === 'classes') return 'My classes';
  return 'Admin view';
}

/** A per-dancer answer: yes, no, or the family provides it. */
export type TrackerAnswer = 'yes' | 'no' | 'family';

export interface TrackerOwnAnswers {
  ordered?: unknown;
  arrived?: unknown;
}

export interface TrackerStepCard extends CostumePickupCard {
  orderStatus?: string | null;
}

const ORDERED = new Set(['ordered', 'arrived', 'picked-up']);
const ARRIVED = new Set(['arrived', 'picked-up']);

/**
 * Ordered, Arrived and Ready for ONE dancer, read the way Kim's Costume report
 * reads them (lib/costumeReport.ts rowFor): her own Yes or No in the report
 * wins, a family-supplied card is the family's, otherwise the class card.
 * Ready is the pickup reading itself (pickupStateFor), so a back-ordered
 * dancer is never ready while her class is.
 */
export function trackerStepAnswers(card: TrackerStepCard, studentId: string, own: TrackerOwnAnswers | null | undefined): {
  ordered: TrackerAnswer; arrived: TrackerAnswer; ready: TrackerAnswer;
} {
  const familyProvides = card.provisionType === 'family-supplied';
  const withOwn = own?.arrived === false ? { ...card, arrivedByDancer: { [studentId]: false } } : card;
  const state = pickupStateFor(withOwn, studentId);
  const pickedUp = state.kind === 'picked-up';
  const order = String(card.orderStatus ?? '');
  const answer = (mine: unknown, classYes: boolean): TrackerAnswer => {
    if (mine === true) return 'yes';
    if (mine === false) return 'no';
    if (familyProvides) return 'family';
    return classYes || pickedUp ? 'yes' : 'no';
  };
  const ready: TrackerAnswer = familyProvides ? 'family'
    : pickedUp || state.kind === 'ready' ? 'yes' : 'no';
  return {
    ordered: answer(own?.ordered, ORDERED.has(order) || card.readyForPickup === true),
    arrived: answer(own?.arrived, ARRIVED.has(order) || card.readyForPickup === true),
    ready,
  };
}

/** Where her bag stands, as the tracker words it. */
export type TrackerPickupState = 'picked-up' | 'ready' | 'not-ready' | 'family-provides' | 'check-with-studio';

export interface TrackerPickup {
  state: TrackerPickupState;
  atMs: number | null;
  via: 'family' | 'desk' | null;
  /** The person who marked it, when the viewer may see the name; never made up. */
  byName: string | null;
  /** True for a grown-up in her own family (only ever sent with a name). */
  byFamily: boolean;
  wholeClass: boolean;
  /**
   * Who walked out with the bag, when the record says (2026-11-03 on). name
   * only when this viewer may see it; note (the desk's words for "someone
   * else") for staff only. Null on older records: nothing is guessed.
   */
  collector: TrackerCollector | null;
}

export interface TrackerCollector {
  kind: 'guardian' | 'dancer' | 'other';
  name: string | null;
  note: string | null;
}

export interface TrackerStep {
  answer: TrackerAnswer;
  /** Who marked the class card's step, and when; null when nobody pressed it here. */
  stamp: CostumeTrailStamp | null;
}

export interface TrackerCostume {
  costumeId: string;
  name: string;
  offeringId: string;
  className: string;
  provisionType: 'studio-ordering' | 'family-supplied' | 'not-set';
  /** Tracked for pickup: a studio-ordered card. */
  tracked: boolean;
  steps: { ordered: TrackerStep; arrived: TrackerStep; ready: TrackerStep };
  /** The whole-class "Picked up" on a card not handed out per dancer. */
  handedOut: CostumeTrailStamp | null;
  triedOnOpen: boolean;
  pieces: FamilyPiece[];
  inHand: CostumeInHand;
  pickup: TrackerPickup;
  /** What this viewer may do here; the server checks again on every tap. */
  canMarkPickup: 'family' | 'desk' | null;
  /**
   * Her own record names the class but the class list does not: a
   * half-finished enrollment. No pickup button until the office finishes it.
   */
  notOnClassList: boolean;
  canUndoPickup: boolean;
  canMarkInHand: boolean;
}

export interface TrackerDancer {
  studentId: string;
  name: string;
  costumes: TrackerCostume[];
}

export interface TrackerPayload {
  view: TrackerView;
  views: TrackerView[];
  dancers: TrackerDancer[];
  /** The list hit the server's cap; the page says so. */
  truncated: boolean;
  /** The viewer is the dancer herself (her dancer sign-in or her own Jr Staff account). */
  asDancer: boolean;
}

const PICKUP_STATES = new Set<TrackerPickupState>(['picked-up', 'ready', 'not-ready', 'family-provides', 'check-with-studio']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function text(value: unknown, max = 200): string {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim().slice(0, max) : '';
}

function ms(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function stampFromWire(raw: unknown): CostumeTrailStamp | null {
  if (!isRecord(raw)) return null;
  return { atMs: ms(raw.atMs), byName: text(raw.byName, 120) || null };
}

function answerFromWire(raw: unknown): TrackerAnswer {
  return raw === 'yes' || raw === 'family' ? raw : 'no';
}

function stepFromWire(raw: unknown): TrackerStep {
  const row = isRecord(raw) ? raw : {};
  return { answer: answerFromWire(row.answer), stamp: stampFromWire(row.stamp) };
}

function viewFromWire(raw: unknown): TrackerView | null {
  return raw === 'mine' || raw === 'classes' || raw === 'all' ? raw : null;
}

function collectorFromWire(raw: unknown): TrackerCollector | null {
  if (!isRecord(raw)) return null;
  if (raw.kind !== 'guardian' && raw.kind !== 'dancer' && raw.kind !== 'other') return null;
  return {
    kind: raw.kind,
    name: raw.kind === 'guardian' ? text(raw.name, 120) || null : null,
    note: raw.kind === 'other' ? text(raw.note, 80) || null : null,
  };
}

function costumeFromWire(raw: unknown): TrackerCostume | null {
  if (!isRecord(raw) || typeof raw.costumeId !== 'string' || !raw.costumeId) return null;
  const steps = isRecord(raw.steps) ? raw.steps : {};
  const p = isRecord(raw.pickup) ? raw.pickup : {};
  const state = PICKUP_STATES.has(p.state as TrackerPickupState) ? p.state as TrackerPickupState : 'check-with-studio';
  const provision = raw.provisionType === 'studio-ordering' || raw.provisionType === 'family-supplied' ? raw.provisionType : 'not-set';
  return {
    costumeId: raw.costumeId,
    name: text(raw.name) || 'Costume',
    offeringId: text(raw.offeringId),
    className: text(raw.className) || 'Class',
    provisionType: provision,
    tracked: raw.tracked === true,
    steps: { ordered: stepFromWire(steps.ordered), arrived: stepFromWire(steps.arrived), ready: stepFromWire(steps.ready) },
    handedOut: stampFromWire(raw.handedOut),
    triedOnOpen: raw.triedOnOpen === true,
    pieces: (Array.isArray(raw.pieces) ? raw.pieces : []).filter(isRecord).map((piece) => ({
      key: text(piece.key, 60),
      label: text(piece.label),
      kind: (['costume', 'shoes', 'tights', 'extra'].includes(String(piece.kind)) ? piece.kind : 'extra') as FamilyPiece['kind'],
    })).filter((piece) => piece.key && piece.label),
    inHand: inHandFrom(raw.inHand),
    pickup: {
      state,
      atMs: state === 'picked-up' ? ms(p.atMs) : null,
      via: p.via === 'family' || p.via === 'desk' ? p.via : null,
      byName: text(p.byName, 120) || null,
      byFamily: p.byFamily === true,
      wholeClass: p.wholeClass === true,
      collector: state === 'picked-up' ? collectorFromWire(p.collector) : null,
    },
    canMarkPickup: raw.canMarkPickup === 'family' || raw.canMarkPickup === 'desk' ? raw.canMarkPickup : null,
    notOnClassList: raw.notOnClassList === true,
    canUndoPickup: raw.canUndoPickup === true,
    canMarkInHand: raw.canMarkInHand === true,
  };
}

/** The lane's answer (and the dancer portal's), checked field by field. */
export function trackerFromWire(payload: unknown): TrackerPayload {
  const body = isRecord(payload) ? payload : {};
  const views = (Array.isArray(body.views) ? body.views : []).map(viewFromWire).filter((v): v is TrackerView => v !== null);
  const view = viewFromWire(body.view) ?? views[0] ?? 'mine';
  const dancers = (Array.isArray(body.dancers) ? body.dancers : []).filter(isRecord).map((d) => ({
    studentId: text(d.studentId, 200),
    name: text(d.name) || 'Dancer',
    costumes: (Array.isArray(d.costumes) ? d.costumes : []).map(costumeFromWire).filter((c): c is TrackerCostume => c !== null),
  })).filter((d) => d.studentId);
  return { view, views: views.length ? views : [view], dancers, truncated: body.truncated === true, asDancer: body.asDancer === true };
}

/** The same costume with her marks replaced after a tap saves. */
export function withInHand(dancers: readonly TrackerDancer[], studentId: string, costumeId: string, inHand: CostumeInHand): TrackerDancer[] {
  return dancers.map((d) => (d.studentId !== studentId ? d : {
    ...d,
    costumes: d.costumes.map((c) => (c.costumeId === costumeId ? { ...c, inHand } : c)),
  }));
}

/** One dancer's card swapped for a fresh read of her, after a pickup tap. */
export function withDancer(dancers: readonly TrackerDancer[], fresh: TrackerDancer): TrackerDancer[] {
  return dancers.map((d) => (d.studentId === fresh.studentId ? fresh : d));
}

/**
 * What a cut list says. The server keeps whole dancers, A to Z by name, and
 * the search only looks at what was sent, so the way to a dancer past the cut
 * is her own profile, whose checklist reads her alone.
 */
export const TRACKER_CUT_TEXT = 'This list was cut short: it shows dancers A to Z by name and stops before the end, so searching here will not find the rest. To see a dancer past the cut, open her profile page. Her costume checklist there shows every costume she wears.';

const STEP_LABELS ={ ordered: 'Ordered', arrived: 'Arrived', ready: 'Ready for pickup' } as const;
export type TrackerStepKey = keyof typeof STEP_LABELS;
export const TRACKER_STEP_KEYS: readonly TrackerStepKey[] = ['ordered', 'arrived', 'ready'];

export function trackerStepLabel(step: TrackerStepKey): string {
  return STEP_LABELS[step];
}

/**
 * The small line under a step: "By Kim, Tue Nov 3, 4:12pm" when the class
 * card's press stands for it, "Done" when nobody's press is on record for her
 * (Kim answered for this dancer alone in the Costume report, or the step
 * predates the trail), "The family provides this" or "Not yet".
 */
export function trackerStepLine(step: TrackerStep): string {
  if (step.answer === 'family') return 'The family provides this';
  if (step.answer === 'no') return 'Not yet';
  if (!step.stamp) return 'Done';
  const who = step.stamp.byName?.trim();
  const when = studioStamp(step.stamp.atMs);
  return [who ? `By ${who}` : '', when].filter(Boolean).join(', ') || 'Done';
}

/**
 * "Picked up by ..." from the record's collectedBy, or plain "Picked up" for
 * an older record that holds none.
 */
function collectorPhrase(c: TrackerCollector | null, dancer: boolean): string {
  if (!c) return 'Picked up';
  if (c.kind === 'guardian') return `Picked up by ${c.name ?? (dancer ? 'your family' : 'her family')}`;
  if (c.kind === 'dancer') return dancer ? 'Picked up by you' : 'Picked up by the dancer';
  return `Picked up by someone else${c.note ? ` (${c.note})` : ''}`;
}

/**
 * WHO PICKED IT UP AND WHO CHECKED IT OUT, AND EXACTLY WHEN. Built only from
 * what the pickup record holds (costumes/{id}.pickups.{studentId}: which
 * door, who tapped, the time, and since 2026-11-03 who collected it):
 *   desk    "Picked up by Jenna Smith, handed out by Kim, Tue Nov 3, 4:12pm"
 *   family  "Picked up by Jenna Smith (family), Tue Nov 3, 4:12pm"
 * When the viewer may not see a name (a teacher, a dancer), the door and the
 * kind alone are said. An older record says only what it holds. Never a
 * made-up name.
 */
export const TRACKER_NOT_ON_CLASS_LIST_TEXT = 'Not on the class list yet. Ask the office to finish their enrollment.';

export function trackerPickupLine(costume: Pick<TrackerCostume, 'pickup' | 'handedOut' | 'provisionType'> & { notOnClassList?: boolean }, input: { dancer?: boolean } = {}): string {
  const p = costume.pickup;
  if (costume.notOnClassList && p.state !== 'picked-up' && p.state !== 'family-provides') {
    return TRACKER_NOT_ON_CLASS_LIST_TEXT;
  }
  const when = studioStamp(p.atMs);
  const dancer = input.dancer === true;
  if (p.state === 'picked-up') {
    if (p.wholeClass) {
      const stamp = costume.handedOut;
      const who = stamp?.byName?.trim();
      const at = studioStamp(stamp?.atMs ?? null);
      return ['Picked up with the whole class', who ? `marked by ${who}` : '', at].filter(Boolean).join(', ');
    }
    if (p.via === 'desk') {
      const handed = p.byName ? `handed out by ${p.byName}` : 'handed out at the front desk';
      return [collectorPhrase(p.collector, dancer), handed, when].filter(Boolean).join(', ');
    }
    if (p.via === 'family') {
      if (p.collector?.kind === 'guardian' && p.collector.name) {
        return [`Picked up by ${p.collector.name} (family)`, when].filter(Boolean).join(', ');
      }
      const who = p.byName
        ? `${p.byName}${p.byFamily ? ' (family)' : ''}`
        : dancer ? 'your family' : 'her family';
      return [`Picked up by ${who}`, when].filter(Boolean).join(', ');
    }
    return [collectorPhrase(p.collector, dancer), when].filter(Boolean).join(', ');
  }
  // The one word set every costume screen uses (lib/costumePickupWording.ts).
  if (p.state === 'ready') return costumePickupStateText('ready');
  if (p.state === 'not-ready') return costumePickupStateText('not-ready');
  if (p.state === 'family-provides') return 'The family provides this costume, nothing to pick up';
  return 'Check with the studio about pickup';
}

/** How much of one dancer's list is done, for the card header. */
export function trackerDancerProgress(dancer: TrackerDancer): { pickedUp: number; tracked: number; waiting: number } {
  const tracked = dancer.costumes.filter((c) => c.tracked);
  return {
    tracked: tracked.length,
    pickedUp: tracked.filter((c) => c.pickup.state === 'picked-up').length,
    waiting: tracked.filter((c) => c.pickup.state === 'ready' && !c.notOnClassList).length,
  };
}

export type TrackerFilter = 'everything' | 'waiting' | 'not-picked-up' | 'picked-up';

/**
 * Search over a dancer's name, her classes and her costume names, every word
 * anywhere, any case; then the pickup filter. Rows with nothing left are
 * dropped.
 */
export function filterTrackerDancers(dancers: readonly TrackerDancer[], query: string, filter: TrackerFilter = 'everything'): TrackerDancer[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const keep = (c: TrackerCostume) => {
    if (filter === 'waiting') return c.pickup.state === 'ready';
    if (filter === 'picked-up') return c.pickup.state === 'picked-up';
    if (filter === 'not-picked-up') return c.tracked && c.pickup.state !== 'picked-up';
    return true;
  };
  const out: TrackerDancer[] = [];
  for (const dancer of dancers) {
    const dancerHit = words.every((w) => dancer.name.toLowerCase().includes(w));
    const costumes = dancer.costumes.filter((c) => {
      if (!keep(c)) return false;
      if (dancerHit) return true;
      const hay = `${dancer.name} ${c.className} ${c.name}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
    if (costumes.length) out.push({ ...dancer, costumes });
  }
  return out;
}

/** True for a card tracked for pickup (labels, the desk, the scan page). */
export function isTrackerTracked(card: Pick<CostumePickupCard, 'provisionType'>): boolean {
  return isCostumeTrackedForPickup(card);
}
